import React, {useEffect, useState} from "react";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import {matchPath} from "react-router";
import {Route, Switch, useHistory, useLocation} from "react-router-dom";
import ApplicationSuggestionsView from "./application-suggestions";
import TechnicalSuggestionsView from "./technical-suggestions";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const SuggestionManagementTabs = {
    application: 'application',
    technical: 'technical',
}

const ManageSuggestionsView = () => {
    const history = useHistory()
    const location = useLocation();
    const [selectedTab, setSelectedTab] = useState(SuggestionManagementTabs.application);
    const {configureNotebookTags} = useConfigureNotebook('products-manage-suggestions-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    const tabs = React.useMemo(() => Object.values(SuggestionManagementTabs).map(e => ({
        value: e,
        name: (function () {
            switch (e) {
                case SuggestionManagementTabs.application:
                    return 'Application Suggestions';
                case SuggestionManagementTabs.technical:
                    return 'Technical Suggestions';
                default:
                    return "";
            }
        })(),
    })), []);


    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productsManageSuggestions);
        configureBehavioralTool((tags) => tags.productsManageSuggestions);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * With each change in the url pathname:
     * - Navigates user to the application suggestion if user was navigated to the base route
     * - if the route is application, then sets the selectedTab to application
     * - if the route is technical, then sets the selectedTab to technical
     */
    useEffect(() => {
        if (matchPath(location.pathname, {path: routes.products.manageSuggestions.base, exact: true})) {
            if (operatorAccessLevels.packages.lookup.applicationSuggestionsList) {
                return history.replace(routes.products.manageSuggestions.application);
            } else if (operatorAccessLevels.packages.lookup.technicalSuggestionsList) {
                return history.replace(routes.products.manageSuggestions.technical);
            } else {
                return history.replace(routes.products.manageSuggestions.base.concat(routes.error.accessDeniedForSection));
            }
        }
        if (matchPath(location.pathname, {path: routes.products.manageSuggestions.technical, exact: true})) {
            if (!operatorAccessLevels.packages.lookup.technicalSuggestionsList) {
                return history.replace(routes.products.manageSuggestions.base.concat(routes.error.accessDeniedForSection));
            }
            return setSelectedTab(SuggestionManagementTabs.technical);
        }
        if (matchPath(location.pathname, {path: routes.products.manageSuggestions.application, exact: true})) {
            if (!operatorAccessLevels.packages.lookup.applicationSuggestionsList) {
                return history.replace(routes.products.manageSuggestions.base.concat(routes.error.accessDeniedForSection));
            }
            return setSelectedTab(SuggestionManagementTabs.application);
        }
    }, [history, location.pathname, operatorAccessLevels.packages.lookup.applicationSuggestionsList, operatorAccessLevels.packages.lookup.technicalSuggestionsList])

    /**
     * Handles changes in the tab:
     * - based on user selection, navigates user to the selected tab content
     */
    const onTabChanged = (e, newValue) => {
        switch (newValue) {
            case "technical":
                history.replace(routes.products.manageSuggestions.technical);
                setSelectedTab(newValue);
                break;
            case "application":
            default:
                history.replace(routes.products.manageSuggestions.application);
                setSelectedTab(newValue);
                break;
        }

    }

    return (
        <>
            <div className={'w-100 mt-3'}>
                <div className={'view-title'}>
                    Product Information Suggestions
                </div>
                {
                    [operatorAccessLevels.packages.lookup.applicationSuggestionsList, operatorAccessLevels.packages.lookup.technicalSuggestionsList].some(Boolean) &&
                    <Tabs className={'mt-4'} value={selectedTab} onChange={onTabChanged}>
                        {
                            tabs?.map((tab) => (
                                <Tab
                                    key={tab.value}
                                    value={tab.value}
                                    label={tab.name}
                                />
                            ))
                        }
                    </Tabs>
                }
            </div>
            <Switch>
                <Route path={routes.products.manageSuggestions.base} exact/>
                {
                    operatorAccessLevels.packages.lookup.applicationSuggestionsList &&
                    <Route path={routes.products.manageSuggestions.application} exact>
                        <ApplicationSuggestionsView/>
                    </Route>
                }
                {
                    operatorAccessLevels.packages.lookup.technicalSuggestionsList &&
                    <Route path={routes.products.manageSuggestions.technical} exact>
                        <TechnicalSuggestionsView/>
                    </Route>
                }
                <Route path={routes.products.manageSuggestions.base.concat(routes.error.accessDeniedForSection)} exact/>
            </Switch>
        </>
    );
}

export default ManageSuggestionsView;
